import React, { useState, useEffect } from "react";
import "../styles/Settings.scss";
import Modal from "react-modal";
import Select from "react-select";
import { getAllClients, getFinalSummary } from "../services/api";
import ExcelJS from "exceljs";

Modal.setAppElement("#root");

const ITEMS_PER_PAGE = 10;

function FinalSummary() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportData, setReportData] = useState([]);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // For download the data
  // const handleExport = () => {
  //   const csv = [
  //     // S.NO, Client Name, Job Title, No.of Requirements, Candidates Joined
  //     "S.NO, Client Name, Job Title, No.of Requirements, Candidates Joined",
  //     ...reportData.map(
  //       (item, index) =>
  //         `${index + 1},${item.client_name || ""},${item.job_title || ""},${item.openings || 0},${item.candidates_joined || 0}`
  //     ),
  //   ].join("\n");
  //   const blob = new Blob([csv], { type: "text/csv" });
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement("a");
  //   a.href = url;
  //   a.download = "FinalSummary.csv";
  //   a.click();
  //   URL.revokeObjectURL(url);
  // };

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Final Summary");

    // Add header row
    worksheet.columns = [
      { header: "S.NO", key: "sno" },
      { header: "Client Name", key: "client_name" },
      { header: "Job Title", key: "job_title" },
      { header: "No.of Requirements", key: "openings" },
      { header: "Candidates Joined", key: "candidates_joined" },
    ];

    // Add data rows
    reportData.forEach((item, index) => {
      worksheet.addRow({
        sno: index + 1,
        date: item.created_at || "",
        client_name: item.client_name || "",
        job_title: item.job_title || "",
        openings: item.openings || 0,
        candidates_joined: item.candidates_joined || 0,
      });
    });

    // Create a Blob from the workbook
    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "FinalSummary.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pageCount) return;
    setPage(newPage);
  };

  // Reset to page 1 on search
  React.useEffect(() => {
    setPage(1);
  }, [selectedClient, startDate, endDate, reportData.length]);

  // for final summary filter along with pagination
  const filteredData = reportData;

  const pageCount = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const paginatedData = filteredData.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // To get All clients
  const fetchClients = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getAllClients();
      if (res && res.status === "S" && Array.isArray(res.result_info)) {
        setClients(res.result_info);
      } else {
        setError(res?.error_info || "Failed to fetch clients");
        setClients([]);
      }
    } catch (err) {
      setError("Failed to fetch clients. Please try again.");
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  // To get final summary data
  const fetchFinalSummary = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getFinalSummary(
        selectedClient?.value || "",
        startDate,
        endDate
      );
      if (res && res.status === "S" && Array.isArray(res.result_info)) {
        setReportData(res.result_info);
      } else {
        setError(res?.message || "Failed to fetch report data");
        setReportData([]);
      }
    } catch (err) {
      setError("Failed to fetch report data. Please try again.");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch clients on component mount
  useEffect(() => {
    fetchClients();
  }, []);

  // Fetch report data when client or dates change
  useEffect(() => {
    // Only fetch report data when a client is selected
    // if (selectedClient) {
    fetchFinalSummary();
    // } else {
    //   setReportData([]);
    // }
  }, [selectedClient, startDate, endDate]);

  // Convert clients to react-select options
  const clientOptions = clients.map((client) => ({
    value: client.id,
    label: client.name,
  }));

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="settings-page">
      <div className="settings-header-row">
        <div className="requisitions-title-group">
          <h1>Final Summary</h1>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button className="export-btn" onClick={handleExport}>
            Export
          </button>
        </div>
      </div>
      <div className="settings-table-controls">
        <div className="filters-row">
          <div className="select-wrapper">
            <Select
              placeholder="Select a client..."
              value={selectedClient}
              onChange={setSelectedClient}
              options={clientOptions}
              styles={{
                control: (base) => ({
                  ...base,
                  minWidth: 220,
                  borderRadius: 6,
                  fontSize: "1rem",
                  background: "#fff",
                  border: "1.5px solid #d1d5db",
                }),
                menu: (base) => ({ ...base, zIndex: 20 }),
              }}
              isClearable
              isSearchable
            />
          </div>
          <div className="date-filters">
            <div className="date-input-wrapper">
              {!startDate && (
                <span className="placeholder-text">Start Date</span>
              )}
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="date-input"
              />
            </div>
            <div className="date-input-wrapper">
              {!endDate && <span className="placeholder-text">End Date</span>}
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="date-input"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="settings-table-wrapper">
        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            Loading final summary data...
          </div>
        ) : (
          <table className="settings-table">
            <thead>
              <tr>
                <th>S.NO</th>
                <th>Client Name</th>
                <th>Job Title</th>
                <th>No. of Requirements</th>
                {/* <th>Candidates Working</th> */}
                <th>Candidates Joined</th>
                {/* <th>Total Requisitions</th> */}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center" }}>
                    {!selectedClient
                      ? "Please select a client to view final summary"
                      : reportData.length === 0
                      ? "No final summary data found for selected client"
                      : "No data matches your date filters"}
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.correl_id || index}>
                    <td>{(page - 1) * ITEMS_PER_PAGE + index + 1}</td>
                    <td>{item.client_name || "--"}</td>
                    <td>{item.job_title || "--"}</td>
                    <td>{item.openings || 0}</td>
                    {/* <td>{item.candidates_working || 0}</td> */}
                    <td>{item.candidates_joined || 0}</td>
                    {/* <td>{item.total_requisitions || 0}</td> */}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination Controls */}
      {pageCount > 1 && (
        <div className="settings-pagination">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            &lt;
          </button>
          {Array.from({ length: pageCount }, (_, i) => (
            <button
              key={i + 1}
              className={"pagination-btn" + (page === i + 1 ? " active" : "")}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === pageCount}
          >
            &gt;
          </button>
        </div>
      )}
    </div>
  );
}

export default FinalSummary;
